import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  createFeatureFlag,
  deleteFeatureFlag,
  deleteFeatureFlagOverride,
  evaluateFeatureFlag,
  getFeatureFlag,
  getMyFeatureFlags,
  searchFeatureFlags,
  toggleFeatureFlagStatus,
  updateFeatureFlag,
  upsertFeatureFlagOverride,
} from '../services/feature-flag.service';
import type {
  CreateFeatureFlagRequest,
  FeatureFlagStatus,
  UpdateFeatureFlagRequest,
  UpsertFeatureFlagOverrideRequest,
} from '../types/feature-flag.types';

const STALE_30S = 30 * 1000;

/* ------------------------------------------------------------------ */
/*  Queries                                                           */
/* ------------------------------------------------------------------ */

/** Resolved flag map for the current user — feeds the runtime `useFeatureFlag` lookup. */
export function useMyFeatureFlags(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.featureFlags.me(),
    queryFn: getMyFeatureFlags,
    enabled: options?.enabled ?? true,
    staleTime: STALE_30S,
  });
}

/** Paginated admin search; `placeholderData` keeps the previous page visible while fetching. */
export function useSearchFeatureFlags(
  filters: { query?: string; status?: FeatureFlagStatus },
  page: number,
  size: number,
) {
  return useQuery({
    queryKey: queryKeys.featureFlags.search(filters, page, size),
    queryFn: () => searchFeatureFlags({ ...filters, page, size }),
    placeholderData: (prev) => prev,
    staleTime: STALE_30S,
  });
}

/** Loads a single flag with its overrides — drives the overrides drawer. */
export function useFeatureFlagDetail(
  id: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.featureFlags.detail(id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('Flag id is required');
      return getFeatureFlag(id);
    },
    enabled: !!id && (options?.enabled ?? true),
  });
}

/** Resolves a flag for a single subsidiary (default vs. override). */
export function useEvaluateFeatureFlag(
  id: string | null | undefined,
  subsidiaryId: string | null | undefined,
) {
  return useQuery({
    queryKey: queryKeys.featureFlags.evaluate(id ?? '', subsidiaryId ?? ''),
    queryFn: () => {
      if (!id || !subsidiaryId) throw new Error('Flag id and subsidiary id are required');
      return evaluateFeatureFlag(id, subsidiaryId);
    },
    enabled: !!id && !!subsidiaryId,
  });
}

/* ------------------------------------------------------------------ */
/*  Mutations — every onSuccess invalidates `featureFlags.all`         */
/* ------------------------------------------------------------------ */

export function useCreateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFeatureFlagRequest) => createFeatureFlag(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.featureFlags.all, refetchType: 'active' });
    },
    onError: (err) => toast.error(extractApiError(err, 'Failed to create feature flag.')),
  });
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFeatureFlagRequest }) =>
      updateFeatureFlag(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.featureFlags.all, refetchType: 'active' });
    },
    onError: (err) => toast.error(extractApiError(err, 'Failed to update feature flag.')),
  });
}

export function useDeleteFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteFeatureFlag(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.featureFlags.all, refetchType: 'active' });
    },
    onError: (err) => toast.error(extractApiError(err, 'Failed to delete feature flag.')),
  });
}

export function useToggleFeatureFlagStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => toggleFeatureFlagStatus(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.featureFlags.all, refetchType: 'active' });
    },
    onError: (err) => toast.error(extractApiError(err, 'Failed to toggle feature flag status.')),
  });
}

export function useUpsertFeatureFlagOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      subId,
      payload,
    }: {
      id: string;
      subId: string;
      payload: UpsertFeatureFlagOverrideRequest;
    }) => upsertFeatureFlagOverride(id, subId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.featureFlags.all, refetchType: 'active' });
    },
    onError: (err) => toast.error(extractApiError(err, 'Failed to save override.')),
  });
}

export function useDeleteFeatureFlagOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, subId }: { id: string; subId: string }) =>
      deleteFeatureFlagOverride(id, subId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.featureFlags.all, refetchType: 'active' });
    },
    onError: (err) => toast.error(extractApiError(err, 'Failed to remove override.')),
  });
}
